import React, { useEffect, useState, useCallback } from 'react'
import { Box, Button } from '@mui/material'
import type {
  AdminPbiReportSearchFilter,
  PbiReportDefinitionViewModel,
} from '@rsmus/ecp-userservice'
import { useTranslation } from 'react-i18next'
import { RsmPaginationPageSizes } from 'src/rsmCoreComponents/components/RsmPagination/RsmPagination'
import {
  PBI_REPORT_COLUMNS,
  mapPbiSortKey,
} from 'src/utils/constants/constants'
import ConfirmRemoveReportDialog from './ConfirmRemoveReportDialog'
import AdminPbiReportResults from './AdminPbiReportResults'
import CustomSuccessAlert from '../forms/Alert/CustomSuccessAlert'
import api from '../../api'
import PowerBIReportModal from './PowerBIReportModal'
import { useUpdateReportSearchResultsMutation } from '../../store/adminSettings/PbiReportSearchService'

/**
 * AdminSettingsPowerBiReport - Admin component for managing Power BI Report Definitions.
 *
 * This component manages PBIReportDefinition records which represent shared report
 * configuration (authentication, tenant settings, security model). Each definition
 * can have multiple PBIReportInstance records managed separately.
 *
 * Key properties handled:
 * - reportName: Unique short name for the report definition
 * - displayName: User-friendly display name
 * - cemAppId: CEM application identifier for authorization
 * - tenantId: Azure tenant GUID
 * - servicePrincipalId: Service principal GUID for Power BI authentication
 * - clientSecretName: Key vault secret name for client credentials
 * - isClientLevelAuthorization: Flag to enable client-level CEM role authorization
 * - effectiveIdentityEnabled: Flag for RLS effective identity
 * - effectiveIdentityRoleName: RLS role name when effective identity is enabled
 *
 * Note: AdminReportDataViewModel maps to PBIReportDefinition schema.
 * The isClientLevelAuthorization field controls whether the report uses
 * application-level or client-level CEM authorization model.
 * Instance checking for authorization model changes is handled in PowerBIReportModal.
 */

// Default values for PowerBI report listing
const DEFAULT_PBI_REPORT_PAGE_NUMBER = 1
const DEFAULT_PBI_REPORT_PAGE_SIZE = 10 as RsmPaginationPageSizes
const DEFAULT_PBI_REPORT_SORT_COLUMN = PBI_REPORT_COLUMNS.REPORT_NAME
const DEFAULT_PBI_REPORT_SORT_DIRECTION: 'asc' | 'desc' = 'asc'

const AdminSettingsPowerBiReport = () => {
  const { t } = useTranslation()
  const [updateReportSearchResults] = useUpdateReportSearchResultsMutation()
  const [openAddReport, setOpenAddReport] = useState(false)
  const [openEditReport, setOpenEditReport] = useState(false)
  const [openRemoveConfirm, setOpenRemoveConfirm] = useState(false)
  const [selectedReport, setSelectedReport] =
    useState<PbiReportDefinitionViewModel | null>(null)
  const [isSaveReportSuccess, setIsSaveReportSuccess] = useState(false)
  const [isEditSaveReportSuccess, setIsEditSaveReportSuccess] = useState(false)
  const [isRemoveSaveReportSuccess, setIsRemoveSaveReportSuccess] =
    useState(false)
  const [reportsData, setReportsData] = useState<{
    data: PbiReportDefinitionViewModel[]
    totalRows: number
  }>({
    data: [],
    totalRows: 0,
  })

  const [pageNumber, setPageNumber] = useState<number>(
    DEFAULT_PBI_REPORT_PAGE_NUMBER,
  )
  const [pageSize, setPageSize] = useState<RsmPaginationPageSizes>(
    DEFAULT_PBI_REPORT_PAGE_SIZE,
  )
  const [sortColumn, setSortColumn] = useState<string>(
    DEFAULT_PBI_REPORT_SORT_COLUMN,
  )
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    DEFAULT_PBI_REPORT_SORT_DIRECTION,
  )
  const [isReportError, setIsReportError] = useState(false)

  // Reset to defaults when key changes
  const resetReportSearch = useCallback(() => {
    setPageNumber(DEFAULT_PBI_REPORT_PAGE_NUMBER)
    setPageSize(DEFAULT_PBI_REPORT_PAGE_SIZE)
    setSortColumn(DEFAULT_PBI_REPORT_SORT_COLUMN)
    setSortDirection(DEFAULT_PBI_REPORT_SORT_DIRECTION)
  }, [])

  const handleSorting = (uiKey: string, dir: 'asc' | 'desc') => {
    setSortColumn(mapPbiSortKey(uiKey) as string)
    setSortDirection(dir)
    setPageNumber(DEFAULT_PBI_REPORT_PAGE_NUMBER)
  }

  const fetchReports = useCallback(async () => {
    const filter: AdminPbiReportSearchFilter = {
      pageNumber,
      pageSize,
      sortBy: sortColumn, // already normalized to backend keys
      isSortAscending: sortDirection === 'asc',
    }
    try {
      setIsReportError(false)
      const result = await updateReportSearchResults(filter).unwrap()
      setReportsData(result)
    } catch {
      // Error handled via isReportError state
      setIsReportError(true)
    }
  }, [
    pageNumber,
    pageSize,
    sortColumn,
    sortDirection,
    updateReportSearchResults,
  ])

  useEffect(() => {
    fetchReports()
  }, [fetchReports])

  useEffect(
    () => () => {
      resetReportSearch()
    },
    [resetReportSearch],
  )

  /**
   * Handles adding a new report definition to the list.
   * The report object includes all PBIReportDefinition properties including
   * isClientLevelAuthorization which determines the authorization model.
   * @param report - The complete report definition data from PowerBIReportModal
   */
  const handleAddNewReport = (report: PbiReportDefinitionViewModel) => {
    setReportsData((prev) => ({
      ...prev,
      data: [report, ...prev.data],
      totalRows: prev.totalRows + 1,
    }))
    setOpenAddReport(false)
    setIsSaveReportSuccess(true)
  }

  /**
   * Handles opening the edit modal for a report definition.
   * Preserves all definition fields including isClientLevelAuthorization
   * when setting selectedReport state for the modal.
   * @param report - The report definition to edit
   */
  const handleEditReport = (report: PbiReportDefinitionViewModel) => {
    setSelectedReport(report)
    setOpenEditReport(true)
  }

  /**
   * Handles updating a report definition after edit.
   * The updated report object from the modal includes all definition fields
   * including isClientLevelAuthorization for proper state synchronization.
   * @param report - The updated report definition data from PowerBIReportModal
   */
  const handleUpdateReport = (report: PbiReportDefinitionViewModel) => {
    setReportsData((prev) => ({
      ...prev,
      data: prev.data.map((r) => (r.id === report.id ? report : r)),
    }))
    setOpenEditReport(false)
    setIsEditSaveReportSuccess(true)
  }

  /**
   * Confirms and executes removal of a report definition.
   * Calls the backend API to remove the PBIReportDefinition record.
   * Note: This will cascade delete associated PBIReportInstance records.
   */
  const confirmRemoveFromTable = async () => {
    if (!selectedReport) return
    try {
      await api.user.report_RemovePowerBIReport(selectedReport.id)
      setReportsData((prev) => ({
        ...prev,
        data: prev.data.filter((r) => r.id !== selectedReport.id),
        totalRows: Math.max(0, prev.totalRows - 1),
      }))
      setIsRemoveSaveReportSuccess(true)
    } finally {
      setOpenRemoveConfirm(false)
      setOpenEditReport(false)
    }
  }

  /**
   * Handles the request to remove a report definition.
   * Opens the confirmation dialog with the selected report.
   * @param report - The report definition to be removed
   */
  const handleAskRemoveReport = (report: PbiReportDefinitionViewModel) => {
    setSelectedReport(report)
    setOpenRemoveConfirm(true)
  }

  return (
    <>
      <Box sx={{ marginBottom: '1rem' }}>
        <Button
          variant="contained"
          data-testid="Btn_AdminSettingsForm_AddNewPowerBIReport"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setOpenAddReport(true)
          }}>
          {t('AdminSettingsForm.AddNewPowerBIReport')}
        </Button>
      </Box>

      <AdminPbiReportResults
        pageNumber={pageNumber}
        pageSize={pageSize as RsmPaginationPageSizes}
        reportsData={reportsData}
        onPageChange={(
          newPageNumber: number,
          newPageSize: RsmPaginationPageSizes,
        ) => {
          setPageNumber(newPageNumber)
          setPageSize(newPageSize)
        }}
        onSorting={handleSorting}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        isMounted={false}
        isReportError={isReportError}
        onEditReport={handleEditReport}
        onRemoveReport={handleAskRemoveReport}
      />
      {/* Modal for adding new report definitions */}
      <PowerBIReportModal
        open={openAddReport}
        onClose={() => setOpenAddReport(false)}
        onSave={handleAddNewReport}
      />
      {/* 
        Modal for editing report definitions.
        initialData includes all PBIReportDefinition fields including isClientLevelAuthorization.
        The modal handles instance checking to prevent authorization model changes when instances exist.
      */}
      <PowerBIReportModal
        open={openEditReport}
        mode="edit"
        initialData={selectedReport || undefined}
        lockShortNameOnEdit
        onClose={() => setOpenEditReport(false)}
        onUpdate={handleUpdateReport}
        onRemove={(reportId) => {
          const report = reportsData.data.find((r) => r.id === reportId)
          if (report) {
            setSelectedReport(report)
            setOpenEditReport(false)
            setOpenRemoveConfirm(true)
          }
        }}
      />
      <CustomSuccessAlert
        open={isSaveReportSuccess}
        close={() => setIsSaveReportSuccess(false)}
        header={t('AdminSettingsForm.PbiReport.ReportSaveSuccess.Title')}
        message={t('AdminSettingsForm.PbiReport.ReportSaveSuccess.Message')}
        testId="Pbireport_ReportSave_Success"
      />
      <CustomSuccessAlert
        open={isEditSaveReportSuccess}
        close={() => setIsEditSaveReportSuccess(false)}
        header={t('AdminSettingsForm.PbiReport.EditReportSaveSuccess.Title')}
        message={t('AdminSettingsForm.PbiReport.EditReportSaveSuccess.Message')}
        testId="Pbireport_EditReportSave_Success"
      />
      <CustomSuccessAlert
        open={isRemoveSaveReportSuccess}
        close={() => setIsRemoveSaveReportSuccess(false)}
        header={t('AdminSettingsForm.PbiReport.RemoveReportSaveSuccess.Title')}
        message={t(
          'AdminSettingsForm.PbiReport.RemoveReportSaveSuccess.Message',
        )}
        testId="Pbireport_RemoveReportSave_Success"
      />
      <ConfirmRemoveReportDialog
        open={openRemoveConfirm}
        onClose={() => setOpenRemoveConfirm(false)}
        onConfirm={confirmRemoveFromTable}
        reportDisplayName={selectedReport?.reportName}
        reportShortName={selectedReport?.reportName}
      />
    </>
  )
}

export default AdminSettingsPowerBiReport
